// src/components/blog/BlogTableOfContents.jsx
import React, { memo, useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react'; // Install lucide-react or use your own icons

const BlogTableOfContents = memo(({ content, isDark }) => {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!content) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const headingElements = doc.querySelectorAll('h2, h3, h4');
    
    const extractedHeadings = Array.from(headingElements).map((heading, index) => {
      const text = heading.textContent;
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || `heading-${index}`;
      return { id, text, level: heading.tagName.toLowerCase() };
    });
    
    setHeadings(extractedHeadings);
  }, [content]);

  useEffect(() => {
    if (!headings.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '0px 0px -60% 0px' }
    );

    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsOpen(false);
  };

  if (!headings.length || !content) return null;

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`lg:hidden fixed bottom-6 right-6 z-50 p-3 rounded-full shadow-lg transition-all duration-200 ${
          isDark 
            ? 'bg-blue-700 hover:bg-blue-800 text-white' 
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
        aria-label="Table of contents"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Desktop Version */}
      <div className={`hidden lg:block fixed left-8 top-1/2 transform -translate-y-1/2 z-20 ${
        isDark ? 'text-gray-300' : 'text-gray-600'
      }`}>
        <div className={`p-4 rounded-xl backdrop-blur-sm w-56 ${
          isDark ? 'bg-black/50 border border-white/10' : 'bg-white/80 border border-gray-200'
        }`}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3 text-blue-500">
            On this page
          </p>
          <nav className="space-y-2 max-h-[70vh] overflow-y-auto">
            {headings.map((heading) => (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                onClick={(e) => handleClick(e, heading.id)}
                className={`block text-sm transition-all duration-200 hover:text-blue-500 cursor-pointer ${
                  activeId === heading.id 
                    ? 'text-blue-500 font-medium' 
                    : isDark ? 'text-gray-300' : 'text-gray-600'
                } ${heading.level === 'h3' ? 'pl-4 text-xs' : ''} ${
                  heading.level === 'h4' ? 'pl-6 text-xs' : ''
                }`}
              >
                {heading.text}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Slide-in Panel */}
      <div className={`
        lg:hidden fixed inset-0 z-40 transition-all duration-300
        ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
      `}>
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50"
          onClick={() => setIsOpen(false)}
        />
        
        {/* Panel */}
        <div className={`
          absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
          w-[90%] max-w-sm max-h-[80vh] p-6 rounded-xl shadow-2xl
          transition-all duration-300 overflow-y-auto
          ${isOpen ? 'scale-100' : 'scale-95'}
          ${isDark 
            ? 'bg-gray-900 border border-white/10 text-gray-300' 
            : 'bg-white border border-gray-200 text-gray-600'
          }
        `}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-blue-500">Table of Contents</h3>
            <button
              onClick={() => setIsOpen(false)}
              className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}
            >
              <X size={20} />
            </button>
          </div>
          
          <nav className="space-y-3">
            {headings.map((heading) => (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                onClick={(e) => handleClick(e, heading.id)}
                className={`block transition-all duration-200 hover:text-blue-500 cursor-pointer ${
                  activeId === heading.id 
                    ? 'text-blue-500 font-medium' 
                    : ''
                } ${heading.level === 'h3' ? 'pl-4 text-sm' : 'text-base'} ${
                  heading.level === 'h4' ? 'pl-8 text-sm' : ''
                }`}
              >
                {heading.text}
              </a>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
});

BlogTableOfContents.displayName = 'BlogTableOfContents';

export default BlogTableOfContents;

